import { CommunicationContent } from "@/components/communications/CommunicationContent.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import { useCommunicationThread } from "@/hooks/useCommunication.ts";
import type { CommunicationDetail } from "@/types/communications.ts";
import { formatDistanceToNow } from "date-fns";
import { ChevronRight } from "lucide-react";
import { useLocation } from "wouter";

/**
 * An email or text conversation — with no chrome of its own, so each host
 * supplies its own header and padding.
 *
 * An exchange of several renders as a list of rows rather than the whole thing
 * stacked open, because opening a group should show what is in it: the reader
 * picks the message they came for instead of scrolling past the others to find
 * it. An exchange of one renders as that message, since a list of one row is a
 * step that only ever leads to the thing behind it.
 *
 * A row opens its message by navigating to the message's own canonical path and
 * letting the surrounding boundary choose the surface — a drill onto the canvas
 * stack where this body is a card on one, and the message's own page where there
 * is no canvas. The row names what it opens; it never names where.
 *
 * Shared by the right sidebar and the canvas card rather than copied, the same
 * arrangement `ContactDetailBody` has.
 */
export const CommunicationThreadBody = ({ communicationId }: { communicationId?: string }) => {
  const [, setLocation] = useLocation();
  const { messages, hasMore, isLoading } = useCommunicationThread({ communicationId });

  // Skeleton rows rather than a spinner: they hold the height the list is about
  // to take, so the card doesn't grow under the cursor as it lands. A spinner
  // would also drag the lottie player into the canvas stack's import graph —
  // this body is reached from the shared card chrome, which is in almost every
  // canvas surface's graph already.
  if (isLoading && !messages) {
    return (
      <div className="flex flex-col gap-1">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }
  if (!messages || messages.length === 0) {
    return <p className="text-sm text-muted-foreground">Conversation not found.</p>;
  }

  // A conversation of one is that message. The row that opened this cannot know
  // how many there are — the count it carries is scoped to one contact, while
  // this is the whole exchange — so the decision belongs here, where the real
  // size is known. A list of one row would be a step that only ever leads to the
  // thing behind it.
  const only = messages.length === 1 ? messages[0] : undefined;
  if (only) return <CommunicationContent data={only} />;

  return (
    <div className="flex flex-col">
      {hasMore && (
        <p className="px-1 py-1 text-center text-xs text-muted-foreground">
          Showing the {messages.length} most recent messages.
        </p>
      )}
      {messages.map((message) => (
        <ThreadMessageRow
          key={message.sourceId}
          message={message}
          onOpen={() => setLocation(`/communications/${message.sourceId}`)}
        />
      ))}
    </div>
  );
};

/**
 * What to call this conversation: an email's subject, or the channel's generic
 * name. Reads the same query the body does (deduped), so a host can title itself
 * without threading the messages back out of the body.
 */
export const useCommunicationThreadTitle = (communicationId?: string): string => {
  const { messages } = useCommunicationThread({ communicationId });
  const first = messages?.[0];
  if (!first) return "Conversation";
  return first.channel === "email" ? first.subject || "Email" : "Conversation";
};

/** Row timestamp: how long ago, which is what distinguishes one reply from the
 *  next when they share a sender and a subject. */
const formatMessageTimestamp = (epochMs: number): string =>
  formatDistanceToNow(new Date(epochMs), { addSuffix: true });

const ThreadMessageRow = ({
  message,
  onOpen,
}: {
  message: CommunicationDetail;
  onOpen: () => void;
}) => {
  const senderName = message.from?.name || message.from?.value || "Unknown";
  const preview = message.plainTextBody.trim().split("\n")[0];

  return (
    <Button
      variant="ghost"
      onClick={onOpen}
      data-statsig-name="communication-thread-message-row"
      className="flex h-auto w-full items-center justify-start gap-2.5 rounded-xl px-2 py-2 text-left"
    >
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-medium">{senderName}</span>
        {preview && (
          <span className="truncate text-xs font-normal text-muted-foreground">{preview}</span>
        )}
      </div>
      <span className="shrink-0 whitespace-nowrap text-xs font-normal text-muted-foreground">
        {formatMessageTimestamp(message.receivedAt)}
      </span>
      <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" />
    </Button>
  );
};
